import { useQuery } from '@tanstack/react-query';
import { LayersIcon } from 'lucide-react';
import { CONNECTOR_PLATFORM_ICONS } from '@/entities/media-connector';
import { platformPlanRulesQueryOptions } from '@/entities/media-guideline';
import { FeatureSidebar } from '@/shared/ui/FeatureSidebar';
import { useTranslation } from 'react-i18next';

export interface GuidelinePlatformNavProps {
  /** The platform being viewed, or null for the cross-platform page. */
  value: string | null;
  onChange: (next: string | null) => void;
}

/**
 * One entry per platform a plan can actually be delivered to, plus the one for
 * everything.
 *
 * A left rail rather than tabs across the top: there are eleven connectors and
 * counting, which a horizontal strip runs out of room for long before the list stops
 * growing. Vertically it just gets longer.
 *
 * At page level rather than over the standard rules alone, because the question a
 * reader has is "what is enforced on Meta" — and that is answered by the house
 * standards and the platform's own rules together, not by either half. Choosing here
 * moves both.
 *
 * The options come from the platforms Delivery has a schema for, which is the same
 * list a guideline may be scoped to. A guideline scoped anywhere else would never run,
 * so an entry for it would be a page that cannot have content.
 */
export function GuidelinePlatformNav({ value, onChange }: GuidelinePlatformNavProps) {
  const { t } = useTranslation('knowledge');
  const { data: platforms, isError } = useQuery(platformPlanRulesQueryOptions());

  const entries = [
    {
      key: null,
      label: t('mediaGuidelines.guidelinePlatformNav.allPlatforms'),
      icon: null,
    },
    ...(platforms ?? []).map((group) => ({
      key: group.platform,
      label: group.label,
      icon: CONNECTOR_PLATFORM_ICONS[group.platform as keyof typeof CONNECTOR_PLATFORM_ICONS],
    })),
  ];

  return (
    <FeatureSidebar label={t('mediaGuidelines.guidelinePlatformNav.platforms')}>
      <FeatureSidebar.Nav>
        {entries.map((entry) => (
          <FeatureSidebar.NavItem
            key={entry.key ?? '__all'}
            active={entry.key === value}
            onClick={() => onChange(entry.key)}
            label={entry.label}
            icon={
              entry.icon ? (
                // Contained, not stretched: several of these are wordmarks rather than
                // square glyphs, and filling a square box skews them.
                <img src={entry.icon} alt="" aria-hidden className="size-md object-contain" />
              ) : (
                <LayersIcon />
              )
            }
          />
        ))}
      </FeatureSidebar.Nav>
      {/* The rail shares its cache entry with `PlatformRulesPanel`, so a failed read
          empties both at once. Without this it degrades to "All platforms" alone, which
          reads as a workspace whose platforms simply are not set up. */}
      {isError && (
        <p role="alert" className="px-3 pb-2 text-xs text-muted-foreground">
          {t('mediaGuidelines.guidelinePlatformNav.platformsCouldNotBeLoaded')}
        </p>
      )}
    </FeatureSidebar>
  );
}
